import React, { MouseEventHandler } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle, XCircle, Info } from "lucide-react";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (id?:string) => void;
  title: string;
  message: React.ReactNode;
  type?: "danger" | "warning" | "success" | "info";
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  showProcessingText?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = "warning",
  confirmText = "Confirm",
  cancelText = "Cancel",
  isLoading = false,
  showProcessingText = true,
}) => {
  if (!isOpen) return null;

  const typeConfig = {
    danger: {
      icon: XCircle,
      iconColor: "text-red-500",
      buttonColor: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
    },
    warning: {
      icon: AlertTriangle,
      iconColor: "text-yellow-500",
      buttonColor: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
    },
    success: {
      icon: CheckCircle,
      iconColor: "text-green-500",
      buttonColor: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
    },
    info: {
      icon: Info,
      iconColor: "text-primary-500",
      buttonColor: "bg-primary-600 hover:bg-primary-700 focus:ring-primary-500",
    },
  };

  const config = typeConfig[type];
  const IconComponent = config.icon;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Portal to document.body (like Modal2): avoids being trapped by any
  // transformed ancestor (e.g. animate-fade-in page roots), which would let
  // sidebar/topbar paint over the dialog and block its clicks.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] no-space flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-soft-hover w-full max-w-md mx-4">
        <div className="p-6">
          {/* Icon and Title */}
          <div className="flex items-center mb-4">
            <div className={`flex-shrink-0 ${config.iconColor}`}>
              <IconComponent className="w-6 h-6" />
            </div>
            <h3 className="ml-3 mb-0 text-lg font-medium text-slate-900">{title}</h3>
          </div>

          {/* Message */}
          <div className="mb-6 text-sm text-slate-600">
            {message}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm as unknown as MouseEventHandler<HTMLButtonElement>}
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${config.buttonColor}`}
            >
              {isLoading && showProcessingText ? "Processing..." : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmationModal;
